import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useShoppingStore } from '../store/shoppingStore';
import { useApiData } from '../hooks/useApiData';
import LoadingSkeleton from '../components/LoadingSkeleton';
import LoadingOverlay from '../components/LoadingOverlay';
import { useApiShopSwitch } from '../hooks/useApiShopSwitch';
import { ShoppingItemData } from '../types/api';
import { useApiShopCreateItem } from '../hooks/useApiShopCreateItem';
import { useApiShopClearCompleted } from '../hooks/useApiShopClearCompleted';
import WebApp from '@twa-dev/sdk';

const ShoppingListPage: React.FC = () => {
  const [newItemText, setNewItemText] = useState('');
  const { items, setItems, addItem, deleteItem } = useShoppingStore();
  const { data, isLoading, error, refetch } = useApiData();
  const { mutate: shopSwitch, isPending: isShopSwitchPending} = useApiShopSwitch();
  const { mutate: createItem, isPending: isCreateItemPending} = useApiShopCreateItem();
  const { mutate: clearCompleted, isPending: isClearCompletedPending} = useApiShopClearCompleted();

  const handleShopSwitch = async (item: ShoppingItemData) => {
      shopSwitch(item, {onSuccess: () => refetch(), onError: () => WebApp.showAlert('Request failed')});
  };

  const handleClearCompleted = async () => {
    clearCompleted(undefined, {onError: () => WebApp.showAlert('Request failed'), onSettled: () => refetch()});
  };

  useEffect(() => {
    if (data?.shopList) {
      setItems(data.shopList);
    }
  }, [data, setItems]);

  const handleAddItem = () => {
    if (newItemText.trim()) {
      createItem(newItemText, {onSuccess: () => {
        setNewItemText('');
        return refetch()
      }, onError: () => WebApp.showAlert('Request failed')});
    }
  };

  if (isLoading) {
    return <LoadingSkeleton input={true} items={3} itemHeight="h-16" />;
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error loading shopping list
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <LoadingOverlay isLoading={isShopSwitchPending || isCreateItemPending || isClearCompletedPending} />
      <h1 className="text-2xl font-bold mb-4">Shopping List</h1>
      
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
          placeholder="Add new item..."
          className="flex-1 p-2 border rounded-lg"
        />
        <button
          onClick={handleAddItem}
          className="bg-blue-500 text-white p-2 rounded-lg"
        >
          <PlusIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="bg-white rounded-lg shadow p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={item.completed}
                onChange={() => handleShopSwitch(item)}
                className="h-5 w-5"
              />
              <span
                className={`${
                  item.completed ? 'line-through text-gray-500' : ''
                }`}
              >
                {item.name}
              </span>
            </div>

          </div>
        ))}
          <button
              onClick={() => handleClearCompleted()}
              className="text-red-500 hover:text-red-700 border border-red-500 rounded-lg p-2 flex justify-center items-center hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4 mr-2" /> Clear complited 
          </button>
      </div>
    </div>
  );
};

export default ShoppingListPage; 